import { AsyncStatus } from 'lib-shared-legacy-types';
import { handleActions } from 'redux-actions';

import type { PhoneNumber as TelephoneNumber } from 'apis/numbers';

import * as telephonyActions from '../../actions/telephony';

type Audio = {
  id: string | null;
  name: string | null;
  chunk_urls: Array<string>;
  master_file_path: string | null;
  chunk_duration_in_secs: number;
  original_as_mp3_url: string | null;
};

export function emptyAudio(): Audio {
  return {
    name: null,
    chunk_urls: [],
    master_file_path: null,
    chunk_duration_in_secs: 0,
    id: null,
    original_as_mp3_url: null,
  };
}

export type GroupNumberT = {
  address_requirement?: {
    proof_required: boolean;
    requirement_type: string;
  };
  area_code: number;
  city_name: string;
  country_code: string;
  group_id: number;
  numbers_type: string;
  region: string;
  stock: number;
};

export type TelephonyStateCountryT = {
  country: string;
  country_code: string;
  country_prefix: number;
};

export type TelephonyStateMetaT = {
  asyncStatus: AsyncStatus;
  changeCountry: AsyncStatus;
  create: AsyncStatus;
  createGroup: AsyncStatus;
  delete: AsyncStatus;
  get: AsyncStatus;
  getByQuery: AsyncStatus;
  list: AsyncStatus;
  listMusic: AsyncStatus;
  nextPage: null | string;
  numTypeSearchStatus: AsyncStatus;
  previousPage: null | string;
  searchStatus: AsyncStatus;
  upload: AsyncStatus;
  verifyExternal: AsyncStatus;
};

export type TelephonyStateSearchResultT = {
  country: TelephonyStateCountryT;
  groups: GroupNumberT[];
  numbers: PhoneNumber[];
};

export type TelephonyStateUIT = {
  buyGroupShown: boolean;
  buyNumberShown: boolean;
  confirmReleaseShown: boolean;
  editNumberShown: boolean;
  isHoldingOpen: boolean;
  isHoldModalOpen: boolean;
  isNewAudioOpen: boolean;
  externalCallerIdModalShown: boolean;
  searchModalShown: boolean;
  verifyPhoneNumberShown: boolean;
};

export type Country = {
  country: string;
  country_code: string;
  country_prefix: number;
};

export type GroupNumber = {
  group_id: number;
  city_name: string | null;
  numbers_type: string;
  country_code: string;
  area_code: number | null;
  stock: number;
  region: string | null;
  address_requirement?: {
    proof_required: boolean;
    requirement_type: string;
  };
};

export type PhoneNumber = {
  phone_number: TelephoneNumber;
  number_type: string | null;
  region: string | null;
  zip_code: string | null;
  carrier: string | null;
};

export type SearchResult = {
  country: Country;
  groups: Array<GroupNumber>;
  numbers: Array<PhoneNumber>;
  numType?: string | null;
  next_page?: string | null;
  prev_page?: string | null;
  limit?: number | null;
};

export type TelephonyState = {
  meta: {
    asyncStatus: AsyncStatus;
    list: AsyncStatus;
    get: AsyncStatus;
    create: AsyncStatus;
    createGroup: AsyncStatus;
    delete: AsyncStatus;
    upload: AsyncStatus;
    listMusic: AsyncStatus;
    getByQuery: AsyncStatus;
    verifyExternal: AsyncStatus;
    searchStatus: AsyncStatus;
    numTypeSearchStatus: AsyncStatus;
    changeCountry: AsyncStatus;
    nextPage: string | null;
    previousPage: string | null;
  };
  ui: {
    isNewAudioOpen: boolean;
    isHoldingOpen: boolean;
    isHoldModalOpen: boolean;
    editNumberShown: boolean;
    confirmReleaseShown: boolean;
    buyGroupShown: boolean;
    buyNumberShown: boolean;
    externalCallerIdModalShown: boolean;
    verifyPhoneNumberShown: boolean;
    searchModalShown: boolean;
  };
  numbers: Array<TelephoneNumber>;
  dynamicOwnNumbers: Array<TelephoneNumber>;
  selectedCountry?: Country;

  searchResult: SearchResult;
  numTypeSearchResult: SearchResult;
  verifyPhoneNumber: {
    phone_number: string;
    code: string;
  };
  selectedNumber: TelephoneNumber | null;
  newNumber: TelephoneNumber | null;
  newNumberLabel: string;

  allAudios: Array<Audio>;
  selectedAudio: Audio;
  jobIds: Array<string>;
};

function defaultCountry(): Country {
  return {
    country: 'United States',
    country_code: 'US',
    country_prefix: 1,
  };
}

const initialState: TelephonyState = {
  meta: {
    asyncStatus: 'succeeded',
    list: 'succeeded',
    get: 'succeeded',
    create: 'succeeded',
    createGroup: 'succeeded',
    delete: 'succeeded',
    upload: 'succeeded',
    listMusic: 'succeeded',
    getByQuery: 'succeeded',
    verifyExternal: 'succeeded',
    searchStatus: 'succeeded',
    numTypeSearchStatus: 'succeeded',
    changeCountry: 'succeeded',
    nextPage: null,
    previousPage: null,
  },
  ui: {
    isNewAudioOpen: false,
    isHoldingOpen: false,
    isHoldModalOpen: false,
    confirmReleaseShown: false,
    editNumberShown: false,
    buyGroupShown: false,
    buyNumberShown: false,
    externalCallerIdModalShown: false,
    verifyPhoneNumberShown: false,
    searchModalShown: false,
  },
  numbers: [],
  dynamicOwnNumbers: [],
  selectedCountry: defaultCountry(),
  searchResult: {
    country: defaultCountry(),
    groups: [],
    numbers: [],
  },
  numTypeSearchResult: {
    country: defaultCountry(),
    groups: [],
    numbers: [],
  },
  verifyPhoneNumber: {
    phone_number: '',
    code: '',
  },
  selectedNumber: null,
  newNumber: null,
  newNumberLabel: '',
  allAudios: [],
  selectedAudio: emptyAudio(),
  jobIds: [],
};

// Destructure the telephony actions.
const {
  telephonyShowEditNumberModal,
  telephonyHideEditNumberModal,

  telephonySetLabel,

  telephonyGetAllNumbers,
  telephonyGetAllNumbersSucceeded,
  telephonyGetAllNumbersFailed,

  telephonyGetNumbersByQuery,
  telephonyGetNumbersByQuerySucceeded,
  telephonyGetNumbersByQueryFailed,

  telephonyShowReleaseNumberModal,
  telephonyHideReleaseNumberModal,

  telephonyBuyGroupNumberDelivered,

  telephonyShowBuyNumberModal,
  telephonyHideBuyNumberModal,

  telephonyShowExternalCallerIdModal,
  telephonyHideExternalCallerIdModal,

  telephonyVerifyExternalCallerId,
  telephonyVerifyExternalCallerIdSucceeded,
  telephonyVerifyExternalCallerIdFailed,

  telephonyShowSearchNumberModal,
  telephonyHideSearchNumberModal,

  telephonyChangeCountryCode,
} = telephonyActions.actions;

// Reducer

export const telephony = handleActions<TelephonyState, any>(
  {
    // Get all numbers

    [telephonyGetAllNumbers.toString()]: (state: TelephonyState) => {
      return { ...state, meta: { ...state.meta, get: 'loading' } };
    },

    [telephonyGetAllNumbersSucceeded.toString()]: (
      state: TelephonyState,
      action: any,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, get: 'succeeded' },
        numbers: action.payload.data.numbers,
      };
    },

    [telephonyGetAllNumbersFailed.toString()]: (state: TelephonyState) => {
      return { ...state, meta: { ...state.meta, get: 'failed' } };
    },

    // Set label

    [telephonySetLabel.toString()]: (state: TelephonyState, action: any) => {
      return {
        ...state,
        newNumberLabel: action.payload,
      };
    },

    // Get numbers by query
    // @TODO: need to check if this can be merged with telephony/TELEPHONY_GET_QUERY_NUMBERS

    [telephonyGetNumbersByQuery.toString()]: (state: TelephonyState) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    [telephonyGetNumbersByQuerySucceeded.toString()]: (
      state: TelephonyState,
      action: any,
    ) => {
      const nextPage =
        action.payload.meta && action.payload.meta.next_page
          ? action.payload.meta.next_page
          : null;
      const prevPage =
        action.payload.meta && action.payload.meta.prev_page
          ? action.payload.meta.prev_page
          : null;

      return {
        ...state,
        dynamicOwnNumbers:
          action.payload.data && Array.isArray(action.payload.data.numbers)
            ? action.payload.data.numbers
            : [],
        meta: {
          ...state.meta,
          asyncStatus: 'succeeded',
          nextPage: nextPage,
          previousPage: prevPage,
        },
      };
    },

    [telephonyGetNumbersByQueryFailed.toString()]: (state: TelephonyState) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    // Release Number

    [telephonyShowReleaseNumberModal.toString()]: (
      state: TelephonyState,
      action: any,
    ) => {
      return {
        ...state,
        ui: { ...state.ui, confirmReleaseShown: true },
        selectedNumber: action.payload,
      };
    },

    [telephonyHideReleaseNumberModal.toString()]: (state: TelephonyState) => {
      return {
        ...state,
        ui: { ...state.ui, confirmReleaseShown: false },
        selectedNumber: null,
      };
    },

    // Edit Number - Update Number name, record outbound calls

    [telephonyShowEditNumberModal.toString()]: (
      state: TelephonyState,
      action: any,
    ) => {
      return {
        ...state,
        ui: { ...state.ui, editNumberShown: true },
        selectedNumber: action.payload.phoneNumber,
      };
    },

    [telephonyHideEditNumberModal.toString()]: (state: TelephonyState) => {
      return {
        ...state,
        ui: { ...state.ui, editNumberShown: false },
      };
    },

    // Show or hide search number modal in Buy Number

    [telephonyShowSearchNumberModal.toString()]: (state: TelephonyState) => {
      return {
        ...state,
        ui: {
          ...state.ui,
          searchModalShown: true,
        },
      };
    },

    [telephonyHideSearchNumberModal.toString()]: (state: TelephonyState) => {
      return {
        ...state,
        ui: {
          ...state.ui,
          searchModalShown: false,
        },
      };
    },

    // Change country code in CountryCodeSelector in Buy Number

    [telephonyChangeCountryCode.toString()]: (
      state: TelephonyState,
      action: any,
    ) => {
      return {
        ...state,
        selectedCountry: action.payload,
      };
    },

    // Show or hide Confirm Buy Number modals

    [telephonyShowBuyNumberModal.toString()]: (
      state: TelephonyState,
      action: any,
    ) => {
      let buyNumberShown = false;
      let buyGroupShown = false;
      let newNumber = null;

      if (action.payload.number !== null) {
        buyNumberShown = true;
        newNumber = action.payload.number;
      } else if (action.payload.group !== null) {
        buyGroupShown = true;
        newNumber = action.payload.group;
      }

      return {
        ...state,
        newNumber: newNumber,
        ui: {
          ...state.ui,
          buyGroupShown: buyGroupShown,
          buyNumberShown: buyNumberShown,
          searchModalShown: false,
        },
      };
    },

    [telephonyHideBuyNumberModal.toString()]: (state: TelephonyState) => {
      return {
        ...state,
        newNumber: null,
        ui: {
          ...state.ui,
          buyGroupShown: false,
          buyNumberShown: false,
        },
      };
    },

    [telephonyBuyGroupNumberDelivered.toString()]: (
      state: TelephonyState,
      action: any,
    ) => {
      const newNumber = Object.assign({}, action.payload.result.data);
      const numbers = state.numbers.slice();

      const index = numbers.findIndex((number: TelephoneNumber) => {
        return number.e164_format === newNumber.e164_format;
      });

      if (index && index > -1) {
        const number = Object.assign({}, numbers[index], newNumber);

        numbers[index] = number;
      } else {
        numbers.unshift(newNumber);
      }

      return {
        ...state,
        numbers: numbers,
        verifyPhoneNumber: {
          phone_number: '',
          code: '',
        },
        meta: { ...state.meta },
        ui: {
          ...state.ui,
          externalCallerIdModalShown: false,
          verifyPhoneNumberShown: false,
        },
      };
    },

    // Verify external caller ID

    [telephonyShowExternalCallerIdModal.toString()]: (
      state: TelephonyState,
    ) => {
      return {
        ...state,
        ui: {
          ...state.ui,
          externalCallerIdModalShown: true,
        },
      };
    },

    [telephonyHideExternalCallerIdModal.toString()]: (
      state: TelephonyState,
    ) => {
      return {
        ...state,
        ui: {
          ...state.ui,
          externalCallerIdModalShown: false,
        },
      };
    },

    [telephonyVerifyExternalCallerId.toString()]: (state: TelephonyState) => {
      return {
        ...state,
        meta: { ...state.meta, verifyExternal: 'loading' },
      };
    },

    [telephonyVerifyExternalCallerIdSucceeded.toString()]: (
      state: TelephonyState,
      action: any,
    ) => {
      const numberToVerify = Object.assign({}, action.payload.data);

      return {
        ...state,
        meta: { ...state.meta, verifyExternal: 'succeeded' },
        ui: {
          ...state.ui,
          externalCallerIdModalShown: true,
          verifyPhoneNumberShown: true,
        },
        verifyPhoneNumber: {
          phone_number: numberToVerify.number.e164_format,
          code: numberToVerify.code,
          name: numberToVerify.name,
        },
      };
    },

    [telephonyVerifyExternalCallerIdFailed.toString()]: (
      state: TelephonyState,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, verifyExternal: 'failed' },
        ui: {
          ...state.ui,
          externalCallerIdModalShown: false,
          verifyPhoneNumberShown: false,
        },
      };
    },
  },

  initialState,
);
